import * as React from "react";
import { Dialog, DialogActions, DialogContent, styled } from "@mui/material";
import Button from "@mui/material/Button";
import { SAVE_DIALOG_AUTO_CLOSE_TIMEOUT } from "../newSettings/saveDialogAutoCloseTimout";
import TextField from "@mui/material/TextField";
import { useEffect, useState } from "react";

//region [[ Styles ]]

const SaveSettingsDialogView = styled(Dialog)({});

const SuccessMessage = styled("div")({
  display: "flex",
  padding: "12px",
  color: "#008A37",
  "&.error": {
    color: "maroon",
  },
});

const AutoCloseAnimationBar = styled("div")({
  width: 0,
  transition: `width ${SAVE_DIALOG_AUTO_CLOSE_TIMEOUT / 1000}s ease-in`,
  background: "#008A37",
  borderRadius: "3px",
  height: "3px",
  "&.active": {
    width: "100%",
  },
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface SaveSettingsDialogProps {
  diff: any;
  saveStatus: SaveStatusObject;
  disabled?: boolean;
  open: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (editedValue: string) => void;
  text: string;
  saveText?: string;
  editable?: boolean;
}

export enum SaveStatus {
  Success,
  Error,
}

export type SaveStatusObject = {
  status: SaveStatus;
  message: string;
};
//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const SaveSettingsDialog = ({
  text,
  diff,
  saveStatus,
  disabled,
  open,
  isSaving,
  ...props
}: SaveSettingsDialogProps) => {
  const [internallyDisabled, setInternallyDisabled] = useState(disabled);
  const [validationError, setValidationError] = useState<string>(null);
  const [editableValue, setEditableValue] = useState("");

  useEffect(() => {
    setInternallyDisabled(disabled);
  }, [disabled]);

  useEffect(() => {
    setEditableValue(JSON.stringify(diff, null, 4));
    setInternallyDisabled(false);
    setValidationError(null);
  }, [diff]);

  const handleJsonChanged = (e) => {
    setEditableValue(e.target.value);
    try {
      const obj = JSON.parse(e.target.value);
      setInternallyDisabled(false);
      setValidationError(null);
    } catch (e) {
      setValidationError(e.message);
      setInternallyDisabled(true);
    }
  };

  return (
    <SaveSettingsDialogView open={open} onClose={props.onCancel}>
      <DialogContent>
        {text}
        {validationError && (
          <>
            <br />
            <span style={{ color: "maroon", fontWeight: 700 }}>
              {validationError}
            </span>
          </>
        )}
        {!props.editable && (
          <pre>
            <code>{editableValue}</code>
          </pre>
        )}
        {props.editable && (
          <TextField
            value={editableValue}
            onChange={handleJsonChanged}
            minRows={25}
            fullWidth
            multiline
          />
        )}
        {isSaving && <b>Saving...</b>}
        {saveStatus && (
          <SuccessMessage
            className={saveStatus.status === SaveStatus.Error ? "error" : ""}
          >
            {saveStatus.message}
          </SuccessMessage>
        )}
      </DialogContent>
      <AutoCloseAnimationBar
        className={saveStatus?.status === SaveStatus.Success ? "active" : ""}
      />
      <DialogActions>
        <Button disabled={internallyDisabled} onClick={props.onCancel}>
          {disabled ? "Close" : "Cancel"}
        </Button>
        <Button
          disabled={internallyDisabled}
          onClick={() => props.onSave(editableValue)}
        >
          {props.saveText || "Save"}
        </Button>
      </DialogActions>
    </SaveSettingsDialogView>
  );
};
